import React from 'react';
import { Stack, ScrollArea, NavLink, Divider, Text } from '@mantine/core';
import {
  IconPick,
  IconAlertTriangle,
  IconClipboardList,
  IconList,
  IconShieldExclamation,
  IconDatabaseImport,
  IconChartHistogram,
  IconAdjustments,
  IconUsers,
  IconBuildingFactory2,
  IconBuildings,
  IconSitemap,
  IconTool,
  IconAddressBook,
  IconBookmarks,
  IconChecklist,
  IconRocket,
  IconChartBar,
  IconHistory,
  IconReportAnalytics,
  IconTargetArrow,
  IconCategory,
  IconCalendarEvent,
  IconDeviceMobile,
  IconFileAnalytics,
  IconMail,
  IconStar,
} from '@tabler/icons-react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { MINEMINER_ROUTES } from '../../routing/routes';
import { useAuth } from '../../hooks/useAuth';

/**
 * MineMinerNavbar Component
 *
 * Sidebar nav for the /MineMiner silo. sprint-125 (epic-019 Sprint D) rewrote
 * the IA into the ADR-001 six-section model (top-to-bottom), matching how a rep
 * thinks about the product:
 *
 *  1. Work Queue — the primary daily rep surface; own section, top.
 *  2. CRM        — Contacts, Companies (stub), Controllers, Operators,
 *                  Contractors, Mines (BASE), Violations.
 *  3. Marketing  — Campaigns, Email Templates, Lists.
 *  4. Tradeshow  — Booth Capture, Events.
 *  5. Leads      — New Mine, Recent Citation, Imminent Danger.
 *  6. Admin      — Audit, Reports, Ingest Runs (?tab=ingest), Scoring Runs,
 *                  Scoring Config, Reps Management. Admin-only — gated on
 *                  `useAuth().isMineMinerAdmin`.
 *
 * This is an IA re-grouping only: every route target is unchanged, deep-link
 * active-state (`isAdminTabActive` for the `?tab=` items) is preserved, and
 * "Mines" still points at BASE (`/MineMiner`), not the post-login Elko default.
 *
 * Silo rule: only MineMiner routes are linked here. Cross-links back to
 * MentionedMe-native pages live in the shared Header user menu.
 */
export const MineMinerNavbar: React.FC = (): React.ReactElement => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isMineMinerAdmin } = useAuth();

  const isPathActive = (path: string): boolean => {
    return location.pathname === path;
  };

  const isAdminTabActive = (tab: string): boolean => {
    return location.pathname === MINEMINER_ROUTES.ADMIN && searchParams.get('tab') === tab;
  };

  const handleNavigate = (path: string): void => {
    navigate(path);
  };

  return (
    <ScrollArea h="calc(100vh - 60px)">
      <Stack gap="xs" p="md">
        {/* sprint-130 — Elko 2026 rep field briefing, featured at the very top
            for the show (temporary; TECH_DEBT pairs with #127/#133 post-Elko).
            Sync-red light variant so it reads as the show CTA, above the IA. */}
        <NavLink
          label="Elko 2026 — Who to Talk To"
          leftSection={<IconStar size={20} stroke={1.5} />}
          active={isPathActive(MINEMINER_ROUTES.ELKO_2026)}
          onClick={(): void => handleNavigate(MINEMINER_ROUTES.ELKO_2026)}
          color="syncRed"
          variant="light"
        />

        <Divider my="xs" />

        {/* Work Queue — primary daily surface; own section, top (ADR-001). */}
        <Text size="xs" c="dimmed" fw={600} tt="uppercase" pl="xs">
          Work Queue
        </Text>
        <NavLink
          label="Work queue"
          leftSection={<IconChecklist size={20} stroke={1.5} />}
          active={isPathActive(MINEMINER_ROUTES.WORK_QUEUE)}
          onClick={(): void => handleNavigate(MINEMINER_ROUTES.WORK_QUEUE)}
          variant="subtle"
        />

        <Divider my="xs" />

        {/* CRM — the people + companies we sell to (folds the old
            Reference + Entities + CRM-contacts groups). */}
        <Text size="xs" c="dimmed" fw={600} tt="uppercase" pl="xs">
          CRM
        </Text>
        <NavLink
          label="Contacts"
          leftSection={<IconAddressBook size={20} stroke={1.5} />}
          active={isPathActive(MINEMINER_ROUTES.CONTACTS_LIST)}
          onClick={(): void => handleNavigate(MINEMINER_ROUTES.CONTACTS_LIST)}
          variant="subtle"
        />
        {/* sprint-125 — Companies "Coming Soon" stub (TECH_DEBT #129). */}
        <NavLink
          label="Companies"
          leftSection={<IconBuildings size={20} stroke={1.5} />}
          active={isPathActive(MINEMINER_ROUTES.COMPANIES_LIST)}
          onClick={(): void => handleNavigate(MINEMINER_ROUTES.COMPANIES_LIST)}
          variant="subtle"
        />
        <NavLink
          label="Controllers"
          leftSection={<IconSitemap size={20} stroke={1.5} />}
          active={isPathActive(MINEMINER_ROUTES.CONTROLLERS_LIST)}
          onClick={(): void => handleNavigate(MINEMINER_ROUTES.CONTROLLERS_LIST)}
          variant="subtle"
        />
        <NavLink
          label="Operators"
          leftSection={<IconBuildingFactory2 size={20} stroke={1.5} />}
          active={isPathActive(MINEMINER_ROUTES.OPERATORS_LIST)}
          onClick={(): void => handleNavigate(MINEMINER_ROUTES.OPERATORS_LIST)}
          variant="subtle"
        />
        <NavLink
          label="Contractors"
          leftSection={<IconTool size={20} stroke={1.5} />}
          active={isPathActive(MINEMINER_ROUTES.CONTRACTORS_LIST)}
          onClick={(): void => handleNavigate(MINEMINER_ROUTES.CONTRACTORS_LIST)}
          variant="subtle"
        />
        {/* sprint-173 (TECH_DEBT #127): the Mines list moved to its own path
            (/MineMiner/mines); the apex /MineMiner now lands on Account Intelligence. */}
        <NavLink
          label="Mines"
          leftSection={<IconList size={20} stroke={1.5} />}
          active={isPathActive(MINEMINER_ROUTES.MINES)}
          onClick={(): void => handleNavigate(MINEMINER_ROUTES.MINES)}
          variant="subtle"
        />
        <NavLink
          label="Violations"
          leftSection={<IconShieldExclamation size={20} stroke={1.5} />}
          active={isPathActive(MINEMINER_ROUTES.VIOLATIONS)}
          onClick={(): void => handleNavigate(MINEMINER_ROUTES.VIOLATIONS)}
          variant="subtle"
        />

        <Divider my="xs" />

        {/* Marketing — the outbound machinery (grouped from the old one-item
            Campaigns group + the CRM Campaigns/Lists items). */}
        <Text size="xs" c="dimmed" fw={600} tt="uppercase" pl="xs">
          Marketing
        </Text>
        <NavLink
          label="Campaigns"
          leftSection={<IconRocket size={20} stroke={1.5} />}
          active={isPathActive(MINEMINER_ROUTES.CAMPAIGNS_LIST)}
          onClick={(): void => handleNavigate(MINEMINER_ROUTES.CAMPAIGNS_LIST)}
          variant="subtle"
        />
        <NavLink
          label="Email Templates"
          leftSection={<IconMail size={20} stroke={1.5} />}
          active={isPathActive(MINEMINER_ROUTES.EMAIL_TEMPLATES_LIST)}
          onClick={(): void => handleNavigate(MINEMINER_ROUTES.EMAIL_TEMPLATES_LIST)}
          variant="subtle"
        />
        <NavLink
          label="Lists"
          leftSection={<IconBookmarks size={20} stroke={1.5} />}
          active={isPathActive(MINEMINER_ROUTES.LISTS_LIST)}
          onClick={(): void => handleNavigate(MINEMINER_ROUTES.LISTS_LIST)}
          variant="subtle"
        />

        <Divider my="xs" />

        {/* Tradeshow — the booth workflow (epic-017). Booth Capture first (the
            at-the-booth action); Events is the organize/review surface. */}
        <Text size="xs" c="dimmed" fw={600} tt="uppercase" pl="xs">
          Tradeshow
        </Text>
        <NavLink
          label="Booth capture"
          leftSection={<IconDeviceMobile size={20} stroke={1.5} />}
          active={isPathActive(MINEMINER_ROUTES.CAPTURE)}
          onClick={(): void => handleNavigate(MINEMINER_ROUTES.CAPTURE)}
          variant="subtle"
        />
        <NavLink
          label="Events"
          leftSection={<IconCalendarEvent size={20} stroke={1.5} />}
          active={isPathActive(MINEMINER_ROUTES.EVENTS_LIST)}
          onClick={(): void => handleNavigate(MINEMINER_ROUTES.EVENTS_LIST)}
          variant="subtle"
        />

        <Divider my="xs" />

        {/* Leads — MSHA-signal prospecting feeds (a once-a-week signal; below
            the day-to-day rep work). */}
        <Text size="xs" c="dimmed" fw={600} tt="uppercase" pl="xs">
          Leads
        </Text>
        <NavLink
          label="New Mine"
          leftSection={<IconPick size={20} stroke={1.5} />}
          active={isPathActive(MINEMINER_ROUTES.LEADS_NEW_MINE)}
          onClick={(): void => handleNavigate(MINEMINER_ROUTES.LEADS_NEW_MINE)}
          variant="subtle"
        />
        <NavLink
          label="Recent Citation"
          leftSection={<IconClipboardList size={20} stroke={1.5} />}
          active={isPathActive(MINEMINER_ROUTES.LEADS_RECENT_CITATION)}
          onClick={(): void => handleNavigate(MINEMINER_ROUTES.LEADS_RECENT_CITATION)}
          variant="subtle"
        />
        <NavLink
          label="Imminent Danger"
          leftSection={<IconAlertTriangle size={20} stroke={1.5} />}
          active={isPathActive(MINEMINER_ROUTES.LEADS_IMMINENT)}
          onClick={(): void => handleNavigate(MINEMINER_ROUTES.LEADS_IMMINENT)}
          variant="subtle"
        />

        <Divider my="xs" />

        {/* Analytics — sprint-147 MSHA 24-month trend review. Rep-level (NOT
            admin-gated): a once-a-week aggregate read, available to every rep. */}
        <Text size="xs" c="dimmed" fw={600} tt="uppercase" pl="xs">
          Analytics
        </Text>
        {/* Account Intelligence — epic-025 Sprint D. The recurring executive briefing
            (top accounts by client value + most-urgent outreach). Rep-level; first in
            the Analytics group, above the raw MSHA lenses it synthesizes. */}
        <NavLink
          label="Account Intelligence"
          leftSection={<IconTargetArrow size={20} stroke={1.5} />}
          active={isPathActive(MINEMINER_ROUTES.ACCOUNT_INTELLIGENCE)}
          onClick={(): void => handleNavigate(MINEMINER_ROUTES.ACCOUNT_INTELLIGENCE)}
          variant="subtle"
        />
        {/* Account Overviews — epic-039 (Canary) increment F, sprint-250. The curated
            per-account briefings. Prefix match keeps the item lit on the detail pages
            (/account-overviews/:accountKey), which exact-match isPathActive would drop. */}
        <NavLink
          label="Account Overviews"
          leftSection={<IconFileAnalytics size={20} stroke={1.5} />}
          active={location.pathname.startsWith(MINEMINER_ROUTES.ACCOUNT_OVERVIEWS)}
          onClick={(): void => handleNavigate(MINEMINER_ROUTES.ACCOUNT_OVERVIEWS)}
          variant="subtle"
        />
        <NavLink
          label="MSHA Analytics"
          leftSection={<IconReportAnalytics size={20} stroke={1.5} />}
          active={isPathActive(MINEMINER_ROUTES.ANALYTICS)}
          onClick={(): void => handleNavigate(MINEMINER_ROUTES.ANALYTICS)}
          variant="subtle"
        />
        {/* Offering Map — sprint-152 (epic-020). How each CFR standard maps to a
            Sync offering, with observed counts + a "Propose a change" capture.
            Rep-level, same as Analytics. */}
        <NavLink
          label="Offering Map"
          leftSection={<IconCategory size={20} stroke={1.5} />}
          active={isPathActive(MINEMINER_ROUTES.OFFERINGS)}
          onClick={(): void => handleNavigate(MINEMINER_ROUTES.OFFERINGS)}
          variant="subtle"
        />

        {/* Admin — back-office tools, gated on isMineMinerAdmin. Audit, Reports,
            and Ingest Runs moved here out of the rep groups (sprint-125). The
            ?tab= deep-link active-state (isAdminTabActive) is preserved. */}
        {isMineMinerAdmin && (
          <>
            <Divider my="xs" />
            <Text size="xs" c="dimmed" fw={600} tt="uppercase" pl="xs">
              Admin
            </Text>
            <NavLink
              label="Audit"
              leftSection={<IconHistory size={20} stroke={1.5} />}
              active={isPathActive(MINEMINER_ROUTES.AUDIT)}
              onClick={(): void => handleNavigate(MINEMINER_ROUTES.AUDIT)}
              variant="subtle"
            />
            <NavLink
              label="Reports"
              leftSection={<IconChartBar size={20} stroke={1.5} />}
              active={isPathActive(MINEMINER_ROUTES.REPORTS)}
              onClick={(): void => handleNavigate(MINEMINER_ROUTES.REPORTS)}
              variant="subtle"
            />
            <NavLink
              label="Ingest Runs"
              leftSection={<IconDatabaseImport size={20} stroke={1.5} />}
              active={isAdminTabActive('ingest')}
              onClick={(): void => handleNavigate(`${MINEMINER_ROUTES.ADMIN}?tab=ingest`)}
              variant="subtle"
            />
            <NavLink
              label="Scoring Runs"
              leftSection={<IconChartHistogram size={20} stroke={1.5} />}
              active={isAdminTabActive('scoring')}
              onClick={(): void => handleNavigate(`${MINEMINER_ROUTES.ADMIN}?tab=scoring`)}
              variant="subtle"
            />
            <NavLink
              label="Scoring Config"
              leftSection={<IconAdjustments size={20} stroke={1.5} />}
              active={isAdminTabActive('config')}
              onClick={(): void => handleNavigate(`${MINEMINER_ROUTES.ADMIN}?tab=config`)}
              variant="subtle"
            />
            <NavLink
              label="Reps Management"
              leftSection={<IconUsers size={20} stroke={1.5} />}
              active={isAdminTabActive('reps')}
              onClick={(): void => handleNavigate(`${MINEMINER_ROUTES.ADMIN}?tab=reps`)}
              variant="subtle"
            />
          </>
        )}
      </Stack>
    </ScrollArea>
  );
};
